/**
 * 设计系统数据模型
 * 定义设计系统相关的数据结构和配置管理
 */

/**
 * 组件状态模型
 */
export class ComponentStateModel {
  constructor(data = {}) {
    this.name = data.name || '';
    this.variant = data.variant || 'default';
    this.size = data.size || 'md';
    this.disabled = data.disabled || false;
    this.loading = data.loading || false;
    this.focused = data.focused || false;
    this.hovered = data.hovered || false;
    this.active = data.active || false;
    this.props = data.props || {};
  }

  /**
   * 更新状态
   * @param {Object} newState - 新状态
   */
  updateState(newState) {
    Object.assign(this, newState);
  }

  /**
   * 重置状态
   */
  reset() {
    this.disabled = false;
    this.loading = false;
    this.focused = false;
    this.hovered = false;
    this.active = false;
  }

  /**
   * 获取CSS类名
   * @returns {string} CSS类名
   */
  getClassName() {
    const classes = [this.name];
    
    if (this.variant !== 'default') {
      classes.push(`${this.name}--${this.variant}`);
    }
    
    if (this.size !== 'md') {
      classes.push(`${this.name}--${this.size}`);
    }
    
    if (this.disabled) classes.push(`${this.name}--disabled`);
    if (this.loading) classes.push(`${this.name}--loading`);
    if (this.focused) classes.push(`${this.name}--focused`);
    if (this.hovered) classes.push(`${this.name}--hovered`);
    if (this.active) classes.push(`${this.name}--active`);
    
    return classes.join(' ');
  }
}

/**
 * 设计令牌模型
 */
export class DesignTokenModel {
  constructor(data = {}) {
    this.category = data.category || ''; // colors, spacing, typography, etc.
    this.name = data.name || '';
    this.value = data.value || '';
    this.description = data.description || '';
    this.cssVariable = data.cssVariable || '';
    this.usage = data.usage || [];
  }

  /**
   * 生成CSS变量名
   * @returns {string} CSS变量名
   */
  generateCSSVariable() {
    if (this.cssVariable) {
      return this.cssVariable;
    }
    
    return `--${this.category}-${this.name.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
  }

  /**
   * 应用到DOM
   */
  applyToDOM() {
    const root = document.documentElement;
    root.style.setProperty(this.generateCSSVariable(), this.value);
  }
}

/**
 * 设计系统配置模型
 */
export class DesignSystemModel {
  constructor(data = {}) {
    this.name = data.name || 'Design System';
    this.version = data.version || '1.0.0';
    this.tokens = new Map();
    this.components = new Map();
    this.breakpoints = data.breakpoints || {
      xs: '0px',
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px'
    };
    this.animations = data.animations || {
      duration: {
        fast: '150ms',
        normal: '300ms',
        slow: '500ms'
      },
      easing: {
        linear: 'linear',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)'
      }
    };
    
    // 初始化默认令牌
    this.initializeDefaultTokens();
  }

  /**
   * 初始化默认设计令牌
   */
  initializeDefaultTokens() {
    // 颜色令牌
    const colorTokens = [
      { category: 'colors', name: 'background', value: '#151515', description: '背景色' },
      { category: 'colors', name: 'foreground', value: '#E9EBDF', description: '前景色' },
      { category: 'colors', name: 'primary', value: '#E9EBDF', description: '主色' },
      { category: 'colors', name: 'secondary', value: '#CBCCC4', description: '次要色' },
      { category: 'colors', name: 'muted', value: '#8B867F', description: '静音色' },
      { category: 'colors', name: 'border', value: '#433E38', description: '边框色' },
      { category: 'colors', name: 'accent', value: '#0E352C', description: '强调色' }
    ];
    
    colorTokens.forEach(token => {
      this.addToken(new DesignTokenModel(token));
    });
    
    // 间距令牌
    const spacingTokens = [
      { category: 'spacing', name: 'xs', value: '0.25rem', description: '超小间距' },
      { category: 'spacing', name: 'sm', value: '0.5rem', description: '小间距' },
      { category: 'spacing', name: 'md', value: '1rem', description: '中等间距' },
      { category: 'spacing', name: 'lg', value: '1.5rem', description: '大间距' },
      { category: 'spacing', name: 'xl', value: '2rem', description: '超大间距' },
      { category: 'spacing', name: '2xl', value: '3rem', description: '特大间距' }
    ];
    
    spacingTokens.forEach(token => {
      this.addToken(new DesignTokenModel(token));
    });
    
    // 字体令牌
    const typographyTokens = [
      { category: 'typography', name: 'fontFamily', value: 'Inter, system-ui, sans-serif', description: '字体族' },
      { category: 'typography', name: 'fontSize-xs', value: '0.75rem', description: '超小字号' },
      { category: 'typography', name: 'fontSize-sm', value: '0.875rem', description: '小字号' },
      { category: 'typography', name: 'fontSize-base', value: '1rem', description: '基础字号' },
      { category: 'typography', name: 'fontSize-lg', value: '1.125rem', description: '大字号' },
      { category: 'typography', name: 'fontSize-xl', value: '1.25rem', description: '超大字号' },
      { category: 'typography', name: 'fontSize-2xl', value: '1.5rem', description: '特大字号' }
    ];
    
    typographyTokens.forEach(token => {
      this.addToken(new DesignTokenModel(token));
    });
  }

  /**
   * 添加设计令牌
   * @param {DesignTokenModel} token - 设计令牌
   */
  addToken(token) {
    const key = `${token.category}.${token.name}`;
    this.tokens.set(key, token);
  }

  /**
   * 获取设计令牌
   * @param {string} category - 令牌类别
   * @param {string} name - 令牌名称
   * @returns {DesignTokenModel|undefined} 设计令牌
   */
  getToken(category, name) {
    const key = `${category}.${name}`;
    return this.tokens.get(key);
  }

  /**
   * 获取某类别的所有令牌
   * @param {string} category - 令牌类别
   * @returns {DesignTokenModel[]} 设计令牌数组
   */
  getTokensByCategory(category) {
    return Array.from(this.tokens.values()).filter(token => token.category === category);
  }

  /**
   * 注册组件
   * @param {string} name - 组件名称
   * @param {Object} config - 组件配置
   */
  registerComponent(name, config) {
    this.components.set(name, {
      name,
      variants: config.variants || ['default'],
      sizes: config.sizes || ['sm', 'md', 'lg'],
      props: config.props || {},
      defaultProps: config.defaultProps || {},
      ...config
    });
  }

  /**
   * 获取组件配置
   * @param {string} name - 组件名称
   * @returns {Object|undefined} 组件配置
   */
  getComponent(name) {
    return this.components.get(name);
  }

  /**
   * 应用所有令牌到DOM
   */
  applyTokensToDOM() {
    this.tokens.forEach(token => {
      token.applyToDOM();
    });
  }

  /**
   * 生成CSS变量
   * @returns {string} CSS变量字符串
   */
  generateCSSVariables() {
    const cssVariables = [];
    
    this.tokens.forEach(token => {
      cssVariables.push(`  ${token.generateCSSVariable()}: ${token.value};`);
    });
    
    return `:root {\n${cssVariables.join('\n')}\n}`;
  }

  /**
   * 导出配置
   * @returns {Object} 配置对象
   */
  export() {
    return {
      name: this.name,
      version: this.version,
      tokens: Array.from(this.tokens.values()).map(token => ({
        category: token.category,
        name: token.name,
        value: token.value,
        description: token.description
      })),
      components: Array.from(this.components.entries()).map(([name, config]) => ({
        name,
        ...config
      })),
      breakpoints: this.breakpoints,
      animations: this.animations
    };
  }

  /**
   * 从配置导入
   * @param {Object} config - 配置对象
   */
  import(config) {
    this.name = config.name || this.name;
    this.version = config.version || this.version;
    this.breakpoints = config.breakpoints || this.breakpoints;
    this.animations = config.animations || this.animations;
    
    // 导入令牌
    if (config.tokens) {
      config.tokens.forEach(tokenData => {
        this.addToken(new DesignTokenModel(tokenData));
      });
    }
    
    // 导入组件
    if (config.components) {
      config.components.forEach(componentData => {
        this.registerComponent(componentData.name, componentData);
      });
    }
  }
}

/**
 * 默认设计系统实例
 */
export const designSystem = new DesignSystemModel({
  name: 'Liu Er Site Design System',
  version: '1.0.0'
});

// 注册默认组件
designSystem.registerComponent('Button', {
  variants: ['primary', 'secondary', 'outline', 'ghost', 'link'],
  sizes: ['sm', 'md', 'lg', 'xl'],
  defaultProps: {
    variant: 'primary',
    size: 'md'
  }
});

designSystem.registerComponent('Input', {
  variants: ['default', 'filled', 'outline'],
  sizes: ['sm', 'md', 'lg'],
  states: ['default', 'success', 'error', 'warning'],
  defaultProps: {
    variant: 'default',
    size: 'md',
    state: 'default'
  }
});

designSystem.registerComponent('Card', {
  variants: ['default', 'elevated', 'outlined'],
  sizes: ['sm', 'md', 'lg'],
  defaultProps: {
    variant: 'default',
    size: 'md'
  }
});